import type { ShopperSearch } from '@/scapi';
import type { FilterValue } from '../types';

const PRICE_RANGE_PATTERN = /\(([^.]*)\.\.([^)]*)\)/;

export function parsePriceRangeValue(value: string): { min?: number; max?: number } {
    const match = value.match(PRICE_RANGE_PATTERN);
    if (!match) return {};

    // eslint-disable-next-line unicorn/prefer-number-coercion
    const min = match[1] ? Number.parseFloat(match[1]) : undefined;
    // eslint-disable-next-line unicorn/prefer-number-coercion
    const max = match[2] ? Number.parseFloat(match[2]) : undefined;

    return {
        min: min !== undefined && Number.isFinite(min) ? min : undefined,
        max: max !== undefined && Number.isFinite(max) ? max : undefined,
    };
}

export function getPriceBoundsFromRefinementValues(values: FilterValue[]): {
    minAllowed?: number;
    maxAllowed?: number;
} {
    let minAllowed: number | undefined;
    let maxAllowed: number | undefined;

    for (const { value } of values) {
        const { min, max } = parsePriceRangeValue(value);
        if (min !== undefined) {
            minAllowed = minAllowed === undefined ? min : Math.min(minAllowed, min);
        }
        if (max !== undefined) {
            maxAllowed = maxAllowed === undefined ? max : Math.max(maxAllowed, max);
        }
    }

    if (minAllowed === undefined || maxAllowed === undefined || maxAllowed <= minAllowed) {
        return {};
    }

    return { minAllowed, maxAllowed };
}

export function getPriceBoundsFromHits(result?: ShopperSearch.schemas['ProductSearchResult']): {
    minAllowed?: number;
    maxAllowed?: number;
} {
    if (!result?.hits?.length) return {};

    const prices = result.hits
        .map((product) => product.price)
        .filter((price): price is number => typeof price === 'number' && price > 0);

    if (prices.length === 0) return {};

    return {
        minAllowed: Math.min(...prices),
        maxAllowed: Math.max(...prices),
    };
}
